import { useEffect } from 'react';
import { useGlobalStore } from '../../stores/global-store';
import { useLoaderStore } from '../../stores/loader-store';
import ErrorBoundary from '../../shared/components/ErrorBoundary';
import ResultItem from './ResultItem';
import { useMp3PreloadWindow } from './useMp3PreloadWindow';

// Port of the results-grid portion of src/app/components/main-content/
// main-content.component.html — the songs loop (incl. the "Similar Songs"
// divider rows) plus the infinite-scroll trigger. Renders as children of the
// #scrollTarget CSS grid owned by MainContent (which also owns the skeleton
// loop and no-more-results alert either side of this component) — matching
// Angular's single flat grid rather than nesting a second grid container.
//
// Infinite scroll: in Angular this lived in app.component.ts — after a 3s
// startup delay it subscribed to 'scroll' on #results-scroll-container
// (throttleTime(100), leading edge) and, once the user passed half the content
// height, called loadMore(), which sets globalState.loadMoreSongsEvent = true
// when nothing is loading and there are more results. The nav-bar reacts to
// loadMoreSongsEvent by fetching the next page. Ported here 1:1 (the scroll
// container itself is rendered by MainContent; coordination happens only via
// the global store's loadMoreSongsEvent field).
//
// Per-item host decorations from the Angular template ([attr.data-result-item],
// class="pop-animate max-w-[400px]", isInView) are rendered by ResultItem
// itself (React components have no host element) — see ResultItem.tsx.

function loadMore() {
  const globalState = useGlobalStore.getState();
  if (!globalState.songsLoading && !useLoaderStore.getState().isLoading && !globalState.showNoMoreResults) {
    globalState.setGlobalField('loadMoreSongsEvent', true);
  }
}

export default function ResultsGrid() {
  const songs = useGlobalStore(s => s.songs);

  // WL MP3 preload window: ids of the ~15 visible MP3 cards to preload. Matching
  // is by song id (not list index), so the "Similar Songs" divider rows — which
  // occupy a slot in `songs` but render no ResultItem and carry id '' — are
  // harmlessly ignored here.
  const preloadWindowIds = useMp3PreloadWindow(songs);

  // Infinite scroll wiring (port of AppComponent.ngAfterViewInit, incl. the 3s
  // delay before the scroll container is looked up).
  useEffect(() => {
    let scrollContainer: HTMLElement | null = null;
    let throttled = false;
    let throttleTimer: ReturnType<typeof setTimeout> | undefined;

    const onScroll = () => {
      // rxjs throttleTime(100): leading-edge — handle the first event, then
      // ignore the rest of the 100ms window.
      if (throttled) {
        return;
      }
      throttled = true;
      throttleTimer = setTimeout(() => {
        throttled = false;
      }, 100);

      // Check if the user has scrolled to at least half the content height
      if (scrollContainer
        && (scrollContainer.scrollTop + scrollContainer.clientHeight) >= (scrollContainer.scrollHeight * 0.5)) {
        loadMore();
      }
    };

    const startupTimer = setTimeout(() => {
      // Get the scroll container element
      scrollContainer = document.getElementById('results-scroll-container');
      scrollContainer?.addEventListener('scroll', onScroll);
    }, 3000);

    return () => {
      clearTimeout(startupTimer);
      clearTimeout(throttleTimer);
      scrollContainer?.removeEventListener('scroll', onScroll);
    };
  }, []);

  return (
    <>
      {songs.map((song, index) =>
        song.showEmptyLine ? (
          <div key={song.id} className="col-span-full flex items-center border-gray-300 my-4">
            <span className="flex-grow border-t border-gray-300"></span>
            <span className="mx-4 text-gray-500">Similar Songs</span>
            <span className="flex-grow border-t border-gray-300"></span>
          </div>
        ) : (
          // Per-card boundary: a single malformed song can't take down the
          // whole grid — it just omits that card (fallback={null}).
          <ErrorBoundary key={song.id} name="result-item" fallback={null}>
            <ResultItem
                isFirst={index === 0}
                shouldPrioritize={index < 25}
                songPositionInList={index}
                preloadAudio={preloadWindowIds.has(song.id)}
                song={song} />
          </ErrorBoundary>
        )
      )}
    </>
  );
}
